import { requestIdleCallback } from '@jxs/utilities';

/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class JxsDeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * JxsComponent 是项目里自定义 Web Component 的基础类。
 *
 * 子类通常继承它来获得两类能力：
 *
 * 1. `refs`：用声明式的 `ref` 属性收集组件内部 DOM 引用。
 *    - `<button ref="submitButton">` 会生成 `this.refs.submitButton`。
 *    - `<li ref="items[]">` 会生成数组 `this.refs.items`。
 *    - 如果声明了 `requiredRefs = ['submitButton']`，但 DOM 中找不到对应 ref，会在初始化时抛错。
 *
 * 2. 声明式事件：在模板中通过 `on:事件名="目标/方法?参数"` 绑定事件，
 *    事件会统一委托到 document，再分发给最近的 JxsComponent 实例或指定组件实例的方法。
 *    详情请看registerEventListeners的注释。
 *
 * 常见用法：
 *
 * @example
 * class JxsCounter extends JxsComponent {
 *   requiredRefs = ['count'];
 *
 *   increase(event) {
 *     this.refs.count.textContent = Number(this.refs.count.textContent) + 1;
 *   }
 *
 *   setStep(data, event) {
 *     console.log(data.step); // 2
 *   }
 * }
 *
 * customElements.define('jxs-counter-component', JxsCounter);
 *
 * @example
 * <jxs-counter-component>
 *   <span ref="count">0</span>
 *   <button on:click="/increase">+1</button>
 *   <button on:click="/setStep?step=2">step = 2</button>
 * </jxs-counter-component>
 *
 * @template {Refs} [T=Refs]
 * @extends {JxsDeclarativeShadowElement}
 */
export class JxsComponent extends JxsDeclarativeShadowElement {
  /**
   * 当前组件内部所有 `ref` DOM 引用的集合。
   *
   * `refs` 会在组件挂载、DOM 变化或 Section Rendering API 更新后自动重新收集。
   * 注意：这里只会收集“属于当前组件”的后代节点，嵌套在其它 JxsComponent 子组件里的 ref
   * 不会被当前组件拿到，避免父子组件之间的 DOM 引用互相污染。
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * 当前组件必需存在的 ref 名称列表。
   *
   * 子类可以声明：
   *
   * @example
   * requiredRefs = ['slides', 'nextButton'];
   *
   * 如果初始化或更新 refs 时找不到这些 ref，会抛出 MissingRefError，帮助尽早发现模板结构错误。
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * 当前组件需要查询/监听的根节点列表。
   *
   * - 没有 Shadow DOM 时，只查询组件自身。
   * - 有 Shadow DOM 时，同时查询 light DOM 中的组件自身和 shadowRoot。
   *
   * 这样组件既可以使用普通 DOM 模板，也可以使用 Declarative Shadow DOM。
   *
   * @returns {(ShadowRoot | JxsComponent<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Web Component 生命周期：组件被插入页面时触发。
   *
   * 这里会完成三件事：
   * 1. 调用父类逻辑，必要时手动 hydrate Declarative Shadow DOM。
   * 2. 全局注册一次声明式事件监听器，支持 `on:click`、`on:change` 等属性写法。
   * 3. 初始化 refs，并在浏览器空闲时启动 MutationObserver，后续 DOM/ref 变化会自动刷新 refs。
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * 组件被 Shopify Section Rendering API 替换/重渲染后可调用的钩子。
   *
   * 调用后会丢弃尚未处理的 mutation 记录，并立即重新收集 refs。
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * Web Component 生命周期：组件从页面移除时触发。
   *
   * 停止监听 DOM 变化，避免组件销毁后 MutationObserver 继续持有引用造成内存泄漏。
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * 重新收集当前组件内部所有带 `ref` 属性的元素，并写入 `this.refs`。
   *
   * ref 规则：
   * - `ref="name"`：保存为单个元素，访问方式为 `this.refs.name`。
   * - `ref="name[]"`：保存为元素数组，访问方式为 `this.refs.name`。
   *
   * 该方法会过滤掉嵌套子组件内部的 ref，只保留离当前组件最近的 ref。
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * 监听组件 DOM 子树中的结构变化以及 `ref` 属性变化。
   *
   * 只要新增/删除节点或元素的 `ref` 属性发生变化，并且变化发生在当前组件作用域内，
   * 就会重新执行 `#updateRefs()`，保证 `this.refs` 始终和 DOM 保持同步。
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;


  /**
   * @param {string} name
   * @param {string | number | boolean | null} [defaultValue]
   * @returns {boolean | string | number | null}
   */
  getDataset(name, defaultValue) {
    const str = this.dataset[name];
    if (str) {
      if (str === 'true') return true;
      if (str === 'false') return false;

      const maybeNumber = Number(str);
      if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

      return str;
    } else {
      if (defaultValue !== undefined) {
        return defaultValue;
      }
      return null;
    }
  }
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `JxsComponent`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `JxsComponent` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof JxsComponent) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * 初始化声明式事件系统。
 *
 * 这个函数只会全局执行一次。它会把一组常用 DOM 事件监听到 document 上，
 * 然后根据触发元素或其祖先元素上的 `on:事件名` 属性，把事件分发到组件实例方法。
 *
 * 支持的事件：
 * - 常规事件：click、change、select、focus、blur、submit、input、keydown、keyup、toggle
 * - 昂贵事件：pointerenter、pointerleave
 *
 * 事件属性语法：
 *
 * 1. 调用最近的 JxsComponent：
 *    @example
 *    <button on:click="/increase">+1</button>
 *
 *    `/increase` 中 selector 为空，所以会调用离该 button 最近的 JxsComponent 实例上的
 *    `increase(event)` 方法。
 *
 * 2. 调用最近匹配 selector 的组件：
 *    @example
 *    <button on:click="jxs-slideshow-component/next">Next</button>
 *
 *    selector 不以 `#` 开头时，会执行 `element.closest(selector)`，然后调用该组件实例上的
 *    `next(event)` 方法。
 *
 * 3. 调用指定 id 的组件：
 *    @example
 *    <button on:click="#cartDrawer/open">Open cart</button>
 *
 *    selector 以 `#` 开头时，会执行 `document.querySelector(selector)`，再调用对应方法。
 *
 * 4. 传递参数给方法：
 *    @example
 *    <button on:click="/select?index=2&active=true">Select</button>
 *    <button on:click="/select/featured">Select featured</button>
 *
 *    - `?index=2&active=true` 会被解析成对象：`{ index: 2, active: true }`
 *    - `/featured` 会被解析成字符串：`"featured"`
 *    - `true` / `false` 会转成布尔值，数字字符串会转成 number
 *
 *    有参数时，方法签名是：`method(data, event)`。
 *    没有参数时，方法签名是：`method(event)`。
 *
 * 事件代理细节：
 * - 监听器使用 capture 阶段，因此 focus/blur 这类不冒泡事件也能被统一处理。
 * - 普通冒泡事件支持从真实点击目标向上查找最近的 `[on\:事件名]` 元素。
 * - pointerenter/pointerleave 触发频率和命中成本较高，只处理事件目标自身带有属性的情况，
 *   不做 `closest()` 向上查找。
 * - `event.currentTarget` 不再代表声明 `on:*` 的元素；因为真实监听器绑定在 document 上，
 *   所以这里的 currentTarget 通常是 document，不能当作“绑定事件的元素”使用。
 * - 如果事件是由子元素触发、但匹配到的 `on:*` 属性在父元素上，会用 Proxy 包装 event，
 *   让回调里的 `event.target` 指向声明了 `on:*` 的元素，而不是最深层的原始触发元素。
 *   因此在组件方法里，通常用 `event.target` 获取绑定 `on:*` 的元素；
 *   只有需要继续查找外层结构时，才使用 `event.target.closest('xxx')`。
 * - 如果需要获取浏览器原始触发元素，可以使用 `event.composedPath?.()[0] ?? event.target`。
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // 从属性值末尾提取参数片段。参数可以使用 query 写法（?a=1&b=true），
        // 也可以使用路径写法（/value）。这里不使用 lookbehind，以兼容 Safari 16.0。
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof JxsComponent) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * 根据事件属性里的参数片段解析传给组件方法的 data。
 *
 * - `?a=1&b=true`：解析成对象 `{ a: 1, b: true }`
 * - `/foo`：解析成单个值 `"foo"`
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * 把字符串参数转换成更合适的 JavaScript 类型。
 *
 * 转换规则：
 * - `"true"` -> `true`
 * - `"false"` -> `false`
 * - 非空数字字符串 -> number
 * - 其它内容保持 string
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {JxsComponent} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}
